import {
  GlobalEvents,
  QueryStore,
  useGlobalEvent
} from "../../chunk-25IAV6Y3.js";
import {
  GlobalState,
  GlobalStore,
  StoreSyncManager
} from "../../chunk-XVNHYOIL.js";
import "../../chunk-QL34RJ4Y.js";
import "../../chunk-3HMZ6FL6.js";
import "../../chunk-A6K3NLMV.js";
import "../../chunk-DGUM43GV.js";

// src/lib/react/use-keyed-store-value.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import equal from "fast-deep-equal";
function useKeyedStoreValue(store, key, defaultValue) {
  const isReadyRef = useRef(store.isReady());
  const [value, setValue] = useState(() => store.get(key, defaultValue));
  const valueRef = useRef(value);
  valueRef.current = value;
  const defaultValueRef = useRef(defaultValue);
  defaultValueRef.current = defaultValue;
  const updateStore = useCallback(
    (valueOrUpdater) => {
      store.set(key, valueOrUpdater);
    },
    [store, key]
  );
  const clearStore = useCallback(() => {
    store.delete(key);
  }, [store, key]);
  const onKeyUpdate = useCallback((newValue) => {
    setValue(newValue ?? defaultValueRef.current);
  }, []);
  const onUpdate = useCallback(() => {
    const newValue = store.get(key) ?? defaultValueRef.current;
    if (!equal(newValue, valueRef.current)) {
      setValue(newValue);
    }
  }, [store, key]);
  useEffect(() => {
    const syncFromStore = () => {
      const currentValue = store.get(key) ?? defaultValueRef.current;
      if (!equal(currentValue, valueRef.current)) {
        setValue(currentValue);
      }
    };
    if (!isReadyRef.current) {
      store.whenReady().then(() => {
        isReadyRef.current = true;
        syncFromStore();
      });
    } else {
      syncFromStore();
    }
    store.on("update", onUpdate);
    store.on(key, onKeyUpdate);
    return () => {
      store.off("update", onUpdate);
      store.off(key, onKeyUpdate);
    };
  }, [store, key, onKeyUpdate, onUpdate]);
  return useMemo(() => {
    return [value, updateStore, clearStore];
  }, [value, updateStore, clearStore]);
}

// src/lib/react/use-global-state.ts
function useGlobalState(key, defaultValue) {
  return useKeyedStoreValue(GlobalState, key, defaultValue);
}

// src/lib/react/use-global-store.ts
import { useRef as useRef2 } from "react";
var useGlobalStore = (storeName, key, defaultValue) => {
  const storeRef = useRef2(GlobalStore.access(storeName));
  return useKeyedStoreValue(storeRef.current, key, defaultValue);
};

// src/lib/react/use-is-ipc-ready.ts
import { useEffect as useEffect2, useState as useState2 } from "react";
var syncPromise = null;
var hasLoggedSyncTime = false;
var IPC_READY_TIME_LABEL = "useIsIpcReady";
function getOrStartSync() {
  if (syncPromise) {
    return syncPromise;
  }
  if (!hasLoggedSyncTime) {
    hasLoggedSyncTime = true;
    console.time(IPC_READY_TIME_LABEL);
  }
  syncPromise = StoreSyncManager.batchSyncWithMain();
  syncPromise.finally(() => {
    if (hasLoggedSyncTime) {
      console.timeEnd(IPC_READY_TIME_LABEL);
      hasLoggedSyncTime = false;
    }
  });
  return syncPromise;
}
var useIsIpcReady = (onReady) => {
  const [storesAndStateReady, setStoresAndStateReady] = useState2(() => {
    return GlobalState.isReady() && GlobalStore.isReady();
  });
  useEffect2(() => {
    if (storesAndStateReady) {
      return;
    }
    getOrStartSync().then(() => {
      setStoresAndStateReady(true);
      onReady?.();
    }).catch((err) => {
      console.error("Error loading stores:", err);
      throw err;
    });
  }, [storesAndStateReady, onReady]);
  return storesAndStateReady;
};

// src/lib/react/use-ipc-state-query.ts
import { useCallback as useCallback2, useEffect as useEffect3, useMemo as useMemo2, useRef as useRef3 } from "react";
import {
  useQuery,
  useQueryClient,
  useMutation
} from "@tanstack/react-query";

// src/lib/react/ipc-query-utils.ts
var IPC_QUERY_PREFIX = "rq";
function serializeQueryKeyPart(part) {
  if (part === null) return "null";
  if (part === void 0) return "undefined";
  if (typeof part === "string") return part;
  if (typeof part === "number") return String(part);
  if (typeof part === "boolean") return String(part);
  return JSON.stringify(part);
}
function deriveIpcKey(queryKey) {
  const parts = queryKey.map(serializeQueryKeyPart);
  return `${IPC_QUERY_PREFIX}:${parts.join("-")}`;
}

// src/lib/react/use-ipc-state-query.ts
function useIpcStateQuery({
  queryKey,
  ipcKey: explicitIpcKey,
  queryFn,
  useIpcInitialData = true,
  ...queryOptions
}) {
  const queryClient = useQueryClient();
  const isLocalUpdateRef = useRef3(false);
  const ipcKey = useMemo2(() => explicitIpcKey ?? deriveIpcKey(queryKey), [explicitIpcKey, queryKey]);
  useEffect3(() => {
    const handleIpcChange = (newValue) => {
      if (isLocalUpdateRef.current) {
        isLocalUpdateRef.current = false;
        return;
      }
      queryClient.setQueryData(queryKey, newValue, { updatedAt: Date.now() });
    };
    QueryStore.on(ipcKey, handleIpcChange);
    return () => {
      QueryStore.off(ipcKey, handleIpcChange);
    };
  }, [queryClient, queryKey, ipcKey]);
  const wrappedQueryFn = useCallback2(async () => {
    const data = await queryFn();
    isLocalUpdateRef.current = true;
    QueryStore.set(ipcKey, data);
    return data;
  }, [queryFn, ipcKey]);
  const getInitialData = useCallback2(() => {
    if (!useIpcInitialData) return void 0;
    return QueryStore.get(ipcKey);
  }, [ipcKey, useIpcInitialData]);
  return useQuery({
    queryKey,
    queryFn: wrappedQueryFn,
    initialData: getInitialData,
    // Mark initial data as potentially stale to trigger background refetch
    initialDataUpdatedAt: useIpcInitialData ? 0 : void 0,
    ...queryOptions
  });
}
function useIpcStateMutation({
  ipcKey: explicitIpcKey,
  queryKey,
  mutationFn,
  onSuccess,
  ...mutationOptions
}) {
  const queryClient = useQueryClient();
  const ipcKey = useMemo2(() => {
    if (explicitIpcKey) return explicitIpcKey;
    if (queryKey) return deriveIpcKey(queryKey);
    throw new Error(
      "useIpcStateMutation: Either ipcKey or queryKey must be provided. Provide explicitIpcKey directly or queryKey to auto-derive via deriveIpcKey."
    );
  }, [explicitIpcKey, queryKey]);
  const wrappedMutationFn = useCallback2(
    async (variables) => {
      const result = await mutationFn(variables);
      QueryStore.set(ipcKey, result);
      return result;
    },
    [mutationFn, ipcKey]
  );
  const wrappedOnSuccess = useCallback2(
    (...args) => {
      const [data] = args;
      if (queryKey) {
        queryClient.setQueryData(queryKey, data, { updatedAt: Date.now() });
      }
      onSuccess?.(...args);
    },
    [queryClient, queryKey, onSuccess]
  );
  return useMutation({
    mutationFn: wrappedMutationFn,
    onSuccess: wrappedOnSuccess,
    ...mutationOptions
  });
}

// src/lib/react/use-ipc-synced-query.ts
import { useEffect as useEffect4, useMemo as useMemo3 } from "react";
import { useQuery as useQuery2, useQueryClient as useQueryClient2 } from "@tanstack/react-query";
function useIpcSyncedQuery({
  queryKey,
  queryFn,
  syncKey: explicitSyncKey,
  prepareBroadcast,
  mergeRemote,
  ...queryOptions
}) {
  const queryClient = useQueryClient2();
  const syncKey = useMemo3(() => explicitSyncKey ?? deriveIpcKey(queryKey), [explicitSyncKey, queryKey]);
  const keyHash = useMemo3(() => JSON.stringify(queryKey), [queryKey]);
  useEffect4(() => {
    const release = acquireSync(queryClient, queryKey, keyHash, syncKey, {
      prepareBroadcast,
      mergeRemote
    });
    return release;
  }, [queryClient, keyHash, syncKey]);
  return useQuery2({
    queryKey,
    queryFn,
    ...queryOptions
  });
}
var activeSyncs = /* @__PURE__ */ new Map();
function acquireSync(queryClient, queryKey, keyHash, syncKey, transforms) {
  let handle = activeSyncs.get(syncKey);
  if (!handle) {
    handle = { count: 0, teardown: setupSync(queryClient, queryKey, keyHash, syncKey, transforms) };
    activeSyncs.set(syncKey, handle);
  }
  handle.count += 1;
  return () => {
    const current = activeSyncs.get(syncKey);
    if (!current) return;
    current.count -= 1;
    if (current.count <= 0) {
      current.teardown();
      activeSyncs.delete(syncKey);
    }
  };
}
function setupSync(queryClient, queryKey, keyHash, syncKey, { prepareBroadcast, mergeRemote }) {
  let applyingRemote = false;
  let scheduled = false;
  const broadcast = () => {
    scheduled = false;
    const data = queryClient.getQueryData(queryKey);
    if (data === void 0) return;
    const payload = prepareBroadcast ? prepareBroadcast(data) : data;
    GlobalEvents.emitToPeers(syncKey, payload);
  };
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (applyingRemote) return;
    if (event.type !== "updated") return;
    if (JSON.stringify(event.query.queryKey) !== keyHash) return;
    if (event.action.type !== "success" || event.action.manual !== true) return;
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(broadcast);
  });
  const applyRemote = (incoming) => {
    applyingRemote = true;
    try {
      queryClient.setQueryData(
        queryKey,
        (prev) => mergeRemote ? mergeRemote(prev, incoming) : incoming,
        { updatedAt: Date.now() }
      );
    } finally {
      applyingRemote = false;
    }
  };
  GlobalEvents.on(syncKey, applyRemote);
  return () => {
    unsubscribe();
    GlobalEvents.off(syncKey, applyRemote);
  };
}

// src/lib/react/use-ipc-store-query.ts
import { useCallback as useCallback3, useEffect as useEffect5, useMemo as useMemo4, useRef as useRef4 } from "react";
import {
  useQuery as useQuery3,
  useQueryClient as useQueryClient3,
  useMutation as useMutation2
} from "@tanstack/react-query";
function useIpcStoreQuery({
  queryKey,
  storeName,
  storeKey: explicitStoreKey,
  queryFn,
  useStoreInitialData = true,
  ...queryOptions
}) {
  const queryClient = useQueryClient3();
  const storeRef = useRef4(GlobalStore.access(storeName));
  const store = storeRef.current;
  const isLocalUpdateRef = useRef4(false);
  const storeKey = useMemo4(() => explicitStoreKey ?? deriveIpcKey(queryKey), [explicitStoreKey, queryKey]);
  useEffect5(() => {
    const handleIpcChange = (newValue) => {
      if (isLocalUpdateRef.current) {
        isLocalUpdateRef.current = false;
        return;
      }
      queryClient.setQueryData(queryKey, newValue, { updatedAt: Date.now() });
    };
    store.on(storeKey, handleIpcChange);
    const handleBulkUpdate = () => {
      const newValue = store.get(storeKey);
      if (newValue !== void 0) {
        queryClient.setQueryData(queryKey, newValue, { updatedAt: Date.now() });
      }
    };
    store.on("update", handleBulkUpdate);
    return () => {
      store.off(storeKey, handleIpcChange);
      store.off("update", handleBulkUpdate);
    };
  }, [queryClient, queryKey, store, storeKey]);
  const wrappedQueryFn = useCallback3(async () => {
    const data = await queryFn();
    isLocalUpdateRef.current = true;
    store.set(storeKey, data);
    return data;
  }, [queryFn, store, storeKey]);
  const getInitialData = useCallback3(() => {
    if (!useStoreInitialData) return void 0;
    return store.get(storeKey);
  }, [store, storeKey, useStoreInitialData]);
  return useQuery3({
    queryKey,
    queryFn: wrappedQueryFn,
    initialData: getInitialData,
    // Mark initial data as potentially stale to trigger background refetch
    initialDataUpdatedAt: useStoreInitialData ? 0 : void 0,
    ...queryOptions
  });
}
function useIpcStoreMutation({
  storeName,
  storeKey: explicitStoreKey,
  queryKey,
  mutationFn,
  onSuccess,
  ...mutationOptions
}) {
  const queryClient = useQueryClient3();
  const storeRef = useRef4(GlobalStore.access(storeName));
  const store = storeRef.current;
  const storeKey = useMemo4(() => {
    if (explicitStoreKey) return explicitStoreKey;
    if (queryKey) return deriveIpcKey(queryKey);
    throw new Error(
      "useIpcStoreMutation: Either storeKey or queryKey must be provided. Provide explicitStoreKey directly or queryKey to auto-derive via deriveIpcKey."
    );
  }, [explicitStoreKey, queryKey]);
  const wrappedMutationFn = useCallback3(
    async (variables) => {
      const result = await mutationFn(variables);
      store.set(storeKey, result);
      return result;
    },
    [mutationFn, store, storeKey]
  );
  const wrappedOnSuccess = useCallback3(
    (...args) => {
      const [data] = args;
      if (queryKey) {
        queryClient.setQueryData(queryKey, data, { updatedAt: Date.now() });
      }
      onSuccess?.(...args);
    },
    [queryClient, queryKey, onSuccess]
  );
  return useMutation2({
    mutationFn: wrappedMutationFn,
    onSuccess: wrappedOnSuccess,
    ...mutationOptions
  });
}

// src/lib/react/use-snapshot-consumer.ts
import { useSyncExternalStore } from "react";
function useSnapshotConsumer(consumer) {
  return useSyncExternalStore(consumer.subscribe, consumer.getSnapshot);
}
export {
  IPC_QUERY_PREFIX,
  deriveIpcKey,
  useGlobalEvent,
  useGlobalState,
  useGlobalStore,
  useIpcStateMutation,
  useIpcStateQuery,
  useIpcStoreMutation,
  useIpcStoreQuery,
  useIpcSyncedQuery,
  useIsIpcReady,
  useSnapshotConsumer
};
//# sourceMappingURL=index.js.map